import { format } from "date-fns"
import PropTypes from "prop-types"
import React from "react"

import NumberedCircle from "src/components/NumberedCircle"
import OverflowMenu from "src/components/OverflowMenu"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { titlecase } from "src/utils/string_helpers"

import CruiseStopIcon from "./CruiseStopIcon"
import editCruiseStopLink from "./cruiseStopLink"
import displayDuration from "./displayDuration"

const CruiseStopItem = ({
  stop,
  previousStop,
  cruisePlanId,
  handleMoveStop,
  index,
}) => {
  const actions = (stop) => {
    return (
      <div className="flex items-center gap-2">
        <a href={editCruiseStopLink(stop, cruisePlanId)}>Edit</a>
        <a
          href={`/account/cruise_plans/${cruisePlanId}/cruise_stops/${stop.id}`}
          data-method="delete"
          data-confirm="Are you sure you want to delete this stop?"
        >
          Delete
        </a>
        <OverflowMenu>
          <OverflowMenu.Item onClick={() => handleMoveStop(stop.id, "up")}>
            Move Up
          </OverflowMenu.Item>
          <OverflowMenu.Item onClick={() => handleMoveStop(stop.id, "down")}>
            Move Down
          </OverflowMenu.Item>
        </OverflowMenu>
      </div>
    )
  }

  const renderPreviousStopDateErrors = () => {
    if (
      previousStop &&
      previousStop.endDate &&
      stop.startDate &&
      format(new Date(previousStop.endDate), "yyyy-MM-dd") !==
        format(new Date(stop.startDate), "yyyy-MM-dd")
    ) {
      return (
        <i
          className="fa-solid fa-triangle-exclamation px-1 text-red-500"
          data-toggle="tooltip"
          data-placement="top"
          title="Possible date issue compared to previous stop end date"
        />
      )
    }
  }

  const timeOf = (time, startOrEnd) => {
    if (!time) return null
    const isStart = startOrEnd === "start"

    if (stop.reservation) {
      return (
        <span className="whitespace-nowrap">
          {isStart ? `after ${time}` : `before ${time}`}
          <i
            className="fa-kit fa-info px-1 text-gray-500"
            title={`Marina ${isStart ? "check-in" : "check-out"} time`}
            data-toggle="tooltip"
            data-placement="top"
          />
        </span>
      )
    } else {
      return <span className="whitespace-nowrap">{time}</span>
    }
  }

  return (
    <tr>
      <td className="px-4">
        <NumberedCircle number={index + 1} />
      </td>
      <td className="px-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center">
            <CruiseStopIcon stopType={stop.type} />
          </div>
          <div>
            {stop.marina ? (
              <a
                href={`/explore/destination/${stop.marina.mcomId}`}
                target="_blank"
                className="text-link"
                rel="noreferrer"
              >
                {stop.name}
              </a>
            ) : (
              stop.name
            )}
          </div>
        </div>
      </td>
      <td className="px-4">{stop.latitude}</td>
      <td className="px-4">{stop.longitude}</td>
      <td className="whitespace-nowrap px-4">
        {stop.startDate && format(new Date(stop.startDate), "MMM dd, yyyy")}
        {renderPreviousStopDateErrors()}
      </td>
      <td className="px-4">
        {stop.endDate && format(new Date(stop.endDate), "MMM dd, yyyy")}
      </td>
      <td className="px-4">{timeOf(stop.arrivalTime, "start")}</td>
      <td className="px-4">{timeOf(stop.departureTime, "end")}</td>
      <td className="px-4">
        {index === 0 ? <span>-</span> : stop.nauticalMiles}
      </td>
      <td className="px-4">{index === 0 ? <span>-</span> : stop.knots}</td>
      <td className="px-4">
        {index === 0 ? <span>-</span> : displayDuration(stop.duration)}
      </td>
      <td className="px-4">
        {stop.reservation && (
          <span className="mr-4 whitespace-nowrap rounded bg-blue-100 px-2 py-0.5 font-bold text-blue-900">
            {titlecase(stop.reservation.status.replace(/_/g, " "))}
          </span>
        )}
      </td>
      <td className="px-4">
        {stop.reservation?.totalPrice &&
          formatCurrencyFromMinorUnits({
            amount: stop.reservation.totalPrice,
          })}
      </td>
      <td className="px-4">{stop.note}</td>
      <td className="px-4">{actions(stop)}</td>
    </tr>
  )
}

CruiseStopItem.propTypes = {
  stop: PropTypes.shape({
    id: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    arrivalTime: PropTypes.string,
    departureTime: PropTypes.string,
    type: PropTypes.string.isRequired,
    name: PropTypes.string,
    note: PropTypes.string,
    marina: PropTypes.shape({
      mcomId: PropTypes.string,
      name: PropTypes.string,
    }),
    title: PropTypes.string,
    reservation: PropTypes.shape({
      status: PropTypes.string,
      totalPrice: PropTypes.number,
    }),
    nauticalMiles: PropTypes.string,
    knots: PropTypes.string,
    duration: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }).isRequired,
  previousStop: PropTypes.shape({
    endDate: PropTypes.string,
  }),
  cruisePlanId: PropTypes.string.isRequired,
  handleMoveStop: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
}

export default CruiseStopItem
